import { FormattedMessage, defineMessages } from 'react-intl';

import { BodyS, HeadlineM, HeadlineS } from '#src/atoms/typography/index.tsx';
import type { GraphQLResult } from '#src/components/form/Form/index.tsx';
import { GraphqlForm } from '#src/components/form/Form/index.tsx';
import { SettingsSection } from '#src/components/settings/SettingsSection/index.tsx';
import { useWalletContext } from '#src/contexts/wallet/index.ts';
import { reloadPage } from '#src/routing/EnsureLatestAppVersion/reloadPage.ts';

import { useDestroyAccount } from './useDestroyAccount.ts';

const messages = defineMessages({
  title: {
    defaultMessage: 'Delete Account',
  },
  description: {
    defaultMessage:
      'Permanently destroy your Sorare account and history of account data. This cannot be undone.',
  },
  cta: {
    defaultMessage: 'Delete Account',
  },
  dialogTitle: {
    defaultMessage: 'Delete Account',
  },
  dialogSubtitle: {
    defaultMessage: 'Are you sure you want to delete your account?',
  },
  dialogDescription: {
    defaultMessage:
      'The deletion of your account will result in the irreversible deletion of all User data associated with the account within 30 days. You must save any Collectibles and Cryptocurrencies stored on the Wallet to an external wallet before any deletion request. Any Collectibles and/or Cryptocurrency stored on your Wallet that have not been transferred out of your account at the date of deletion will be permanently irretrievable. Sorare will not be responsible for the permanent loss of any Collectibles and/or Cryptocurrency that was not saved prior to a deletion request.',
  },
  dialogCta: {
    defaultMessage: 'Delete Account',
  },
});

export const DeleteAccount = () => {
  const destroyAccount = useDestroyAccount();
  const { getPassword, logOut } = useWalletContext();

  const submitDeleteAccount = async (
    attributes: any,
    doOnResult: (result: GraphQLResult) => void
  ) => {
    const currentPasswordHash = await getPassword();
    if (!currentPasswordHash) return;

    const { data } = await destroyAccount({ password: currentPasswordHash });

    if (data) {
      doOnResult(data.destroyAccount!);
      // make sure we clean up any leftovers from the user
      reloadPage();
    }
  };
  return (
    <GraphqlForm
      onSubmit={(attributes, doOnResult) => {
        submitDeleteAccount(attributes, doOnResult);
      }}
      onSuccess={() => {
        logOut();
      }}
      askForConfirmation
      dialogTitle={
        <HeadlineM as="h4">
          <FormattedMessage {...messages.dialogTitle} />
        </HeadlineM>
      }
      dialogSubtitle={
        <HeadlineS as="h5">
          <FormattedMessage {...messages.dialogSubtitle} />
        </HeadlineS>
      }
      confirmationMessage={
        <BodyS>
          <FormattedMessage {...messages.dialogDescription} />
        </BodyS>
      }
      dialogCta={<FormattedMessage {...messages.dialogCta} />}
      dialogCtaProps={{ color: 'red' }}
      render={(Error, SubmitButton) => (
        <SettingsSection {...messages}>
          <Error code />
          <div>
            <SubmitButton size="small" color="red">
              <FormattedMessage {...messages.cta} />
            </SubmitButton>
          </div>
        </SettingsSection>
      )}
    />
  );
};
